// DriftEventsList.js — Filterable drift events table with lifecycle actions
import React, { useState, useEffect, useCallback } from 'react';
import { getEvents, acknowledgeEvent, snoozeEvent, resolveEvent } from '../services/driftService';
import './DriftEventsList.css';

const SNOOZE_OPTIONS = [
    { label: '1 hour', hours: 1 },
    { label: '4 hours', hours: 4 },
    { label: '1 day', hours: 24 },
    { label: '1 week', hours: 168 }
];

function DriftEventsList({ assetId, assets, compact }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [showSnoozeMenu, setShowSnoozeMenu] = useState(null);

    // Filters
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterAsset, setFilterAsset] = useState('all');

    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await getEvents(assetId);
            setEvents(result);
        } catch (err) {
            console.error('Failed to fetch drift events:', err);
            setError('Failed to load drift events');
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleAcknowledge = async (eventId) => {
        try {
            setActionLoading(eventId);
            await acknowledgeEvent(eventId);
            setEvents(prev => prev.map(e =>
                e.eventId === eventId
                    ? { ...e, status: 'acknowledged', acknowledgedAt: new Date().toISOString() }
                    : e
            ));
        } catch (err) {
            console.error('Failed to acknowledge event:', err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleSnooze = async (eventId, hours) => {
        try {
            setActionLoading(eventId);
            setShowSnoozeMenu(null);
            const snoozedUntil = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
            await snoozeEvent(eventId, snoozedUntil);
            setEvents(prev => prev.map(e =>
                e.eventId === eventId
                    ? { ...e, status: 'snoozed', snoozedUntil }
                    : e
            ));
        } catch (err) {
            console.error('Failed to snooze event:', err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleResolve = async (eventId) => {
        try {
            setActionLoading(eventId);
            await resolveEvent(eventId);
            setEvents(prev => prev.map(e =>
                e.eventId === eventId
                    ? { ...e, status: 'resolved', resolvedAt: new Date().toISOString(), resolvedBy: 'manual' }
                    : e
            ));
        } catch (err) {
            console.error('Failed to resolve event:', err);
        } finally {
            setActionLoading(null);
        }
    };

    // Apply filters
    let filtered = events;
    if (filterSeverity !== 'all') {
        filtered = filtered.filter(e => e.ruleSeverity === filterSeverity);
    }
    if (filterStatus !== 'all') {
        filtered = filtered.filter(e => e.status === filterStatus);
    }
    if (filterAsset !== 'all') {
        filtered = filtered.filter(e => e.assetId === filterAsset);
    }

    const formatDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
            ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const getAssetName = (assetId) => {
        if (!assets) return assetId;
        const a = assets.find(a => a.assetId === assetId || a.rowKey === assetId);
        return a?.target || a?.name || assetId;
    };

    if (loading) {
        return (
            <div className="drift-events-loading">
                <div className="loading-spinner"></div>
                <span>Loading drift events...</span>
            </div>
        );
    }

    if (error) {
        return <div className="drift-events-error">{error}</div>;
    }

    return (
        <div className={`drift-events-container ${compact ? 'drift-events-compact' : ''}`}>
            {/* Filter bar */}
            {!compact && (
                <div className="drift-events-filters">
                    <select
                        value={filterSeverity}
                        onChange={(e) => setFilterSeverity(e.target.value)}
                        className="drift-events-filter"
                    >
                        <option value="all">All Severities</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                        <option value="info">Info</option>
                    </select>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="drift-events-filter"
                    >
                        <option value="all">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="acknowledged">Acknowledged</option>
                        <option value="snoozed">Snoozed</option>
                        <option value="resolved">Resolved</option>
                    </select>

                    {!assetId && assets && (
                        <select
                            value={filterAsset}
                            onChange={(e) => setFilterAsset(e.target.value)}
                            className="drift-events-filter"
                        >
                            <option value="all">All Assets</option>
                            {assets.map(a => (
                                <option key={a.assetId || a.rowKey} value={a.assetId || a.rowKey}>
                                    {a.target || a.name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            )}

            {/* Events table */}
            {filtered.length === 0 ? (
                <div className="drift-events-empty">
                    <p>No drift events detected yet.</p>
                    <p className="drift-events-empty-sub">
                        Events appear when drift rules detect changes in your monitored assets.
                    </p>
                </div>
            ) : (
                <div className="drift-events-table-wrap">
                    <table className="drift-events-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                {!assetId && <th>Asset</th>}
                                <th>Rule</th>
                                <th>Severity</th>
                                <th>Detail</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(event => (
                                <tr key={event.eventId} className="drift-events-row">
                                    <td data-label="Date">{formatDate(event.createdAt)}</td>
                                    {!assetId && <td data-label="Asset">{getAssetName(event.assetId)}</td>}
                                    <td data-label="Rule">
                                        <span className="drift-events-rule-name">{event.ruleName || '—'}</span>
                                    </td>
                                    <td data-label="Severity">
                                        <span className="drift-severity-dot" data-severity={event.ruleSeverity}></span>
                                        <span className="drift-events-severity-text">{event.ruleSeverity}</span>
                                    </td>
                                    <td data-label="Detail">
                                        <span className="drift-events-detail">{event.detail}</span>
                                    </td>
                                    <td data-label="Status">
                                        <span className="drift-event-badge" data-status={event.status}>
                                            {event.status}
                                        </span>
                                    </td>
                                    <td data-label="Actions">
                                        <div className="drift-events-actions">
                                            {(event.status === 'open') && (
                                                <button
                                                    className="drift-events-action-btn"
                                                    onClick={() => handleAcknowledge(event.eventId)}
                                                    disabled={actionLoading === event.eventId}
                                                    title="Acknowledge"
                                                >
                                                    Ack
                                                </button>
                                            )}
                                            {(event.status === 'open' || event.status === 'acknowledged') && (
                                                <div className="drift-events-snooze-wrap">
                                                    <button
                                                        className="drift-events-action-btn"
                                                        onClick={() => setShowSnoozeMenu(
                                                            showSnoozeMenu === event.eventId ? null : event.eventId
                                                        )}
                                                        disabled={actionLoading === event.eventId}
                                                        title="Snooze"
                                                    >
                                                        Snooze
                                                    </button>
                                                    {showSnoozeMenu === event.eventId && (
                                                        <div className="drift-events-snooze-menu">
                                                            {SNOOZE_OPTIONS.map(opt => (
                                                                <button
                                                                    key={opt.hours}
                                                                    className="drift-events-snooze-option"
                                                                    onClick={() => handleSnooze(event.eventId, opt.hours)}
                                                                >
                                                                    {opt.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {event.status !== 'resolved' && (
                                                <button
                                                    className="drift-events-action-btn resolve"
                                                    onClick={() => handleResolve(event.eventId)}
                                                    disabled={actionLoading === event.eventId}
                                                    title="Resolve"
                                                >
                                                    Resolve
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default DriftEventsList;
